// Shared manual-entry spend math — used by BOTH the match-stats spend line and the pre-submit
// warning, so the count/%/$ always agree between them.
//
// Keyed on the NEGOTIATION Starting Price (NSP): a row is MANUAL ENTRY when it has no smart-baseline
// match (unresolved → no smart starting price) OR the buyer typed a manual negotiation override.
// Read synchronously from the in-session stores (resolved-name bridge + per-row config), so it's
// accurate without re-running the match.
import { ID } from 'types';
import { getResolvedItemNameToUse, getResolvedNameForLineItem } from './nsbResolvedItemBridge';
import { getNsbRowConfig } from './nsbRowConfigStore';
import { MANUAL_ENTRY } from './nsbSelectionStore';

export interface ManualSpendLineItem {
  extendedQuantity?: number | string | null;
  id: ID;
  name?: string | null;
  price?: number | string | null;
}

export interface ManualSpendSummary {
  manualCount: number;
  manualPct: number; // 0-100: share of TOTAL spend that is on manual-entry baselines
  manualSpend: number;
  totalCount: number;
  totalSpend: number;
}

// A row is "manual entry" by its NEGOTIATION Starting Price (NSP): no match → no smart starting
// price, or the buyer typed a manual negotiation override. (Stat is keyed on NSP, not the baseline.)
const isManualEntryRow = (li: ManualSpendLineItem, eventId: ID): boolean => {
  const id = String(li.id);
  // By NAME first, id second. Both are recorded when a row resolves, but the id-keyed entry is filed
  // against whatever id the row had at the time, and an import's save-and-reload replaces every
  // `new-<uuid>` with a real one - orphaning them. Reading id-only counted a fully matched event as
  // hundreds of manual rows on the first save, and only came right after a reload rehydrated the ids.
  const resolved = getResolvedItemNameToUse(li.name ?? '') || getResolvedNameForLineItem(id);
  if (!resolved) return true;
  return getNsbRowConfig(`${eventId}_${id}`)?.negotiation === MANUAL_ENTRY;
};

// Total Line Item Price per row = price * extended_quantity (mirrors buyerInitialTableData), with the
// same defaults (qty → 1, price → 0).
const rowSpend = (li: ManualSpendLineItem): number => Number(li.price || 0) * Number(li.extendedQuantity || 1);

export const computeManualEntrySpend = (lineItems: ManualSpendLineItem[], eventId: ID): ManualSpendSummary => {
  let totalSpend = 0;
  let manualSpend = 0;
  let manualCount = 0;
  lineItems.forEach(li => {
    const spend = rowSpend(li);
    totalSpend += spend;
    if (isManualEntryRow(li, eventId)) {
      manualSpend += spend;
      manualCount += 1;
    }
  });
  const manualPct = totalSpend > 0 ? Math.round((manualSpend / totalSpend) * 100) : 0;
  return { manualCount, manualPct, manualSpend, totalCount: lineItems.length, totalSpend };
};
